import { idb, saveMyReport, type OutboxItem } from './db';

/**
 * Reports (emergency, crop damage) always go through this outbox: saved on the device first, then sent.
 * Offline, they wait and are retried when the connection returns (online event, app focus, every 30s,
 * and Background Sync in the service worker where supported). Used from both the page and sw.ts.
 */

export type SubmitResult =
  | { status: 'sent'; ref: string; token: string; response: unknown }
  | { status: 'queued' }
  | { status: 'rejected'; message: string; fields?: Record<string, string> };

type Listener = () => void;
const listeners = new Set<Listener>();
export const onOutboxChange = (l: Listener) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const emit = () => listeners.forEach((l) => l());

async function csrf(): Promise<string> {
  if (typeof document !== 'undefined') {
    const m = document.cookie.match(/(?:^|; )ili_csrf=([^;]*)/);
    if (m) return decodeURIComponent(m[1]);
  }
  const r = await fetch('/api/csrf', { credentials: 'same-origin' });
  return ((await r.json()) as { token: string }).token;
}

type SendOutcome = { kind: 'ok'; json: { ref: string; trackingToken: string } & Record<string, unknown> } | { kind: 'rejected'; message: string; fields?: Record<string, string> } | { kind: 'retry'; message: string };

async function send(item: OutboxItem): Promise<SendOutcome> {
  const form = new FormData();
  form.set('data', JSON.stringify(item.data));
  for (const f of item.files) form.append(f.field, f.blob, f.name);
  const res = await fetch(`/api${item.path}`, {
    method: 'POST',
    body: form,
    credentials: 'same-origin',
    headers: { 'X-CSRF-Token': await csrf(), 'X-Device-Id': item.deviceId, Accept: 'application/json' },
  });
  const body = await res.json().catch(() => ({}));
  if (res.ok) return { kind: 'ok', json: body };
  if (res.status >= 400 && res.status < 500 && ![403, 408, 429].includes(res.status)) return { kind: 'rejected', message: body.message ?? 'The report was not accepted.', fields: body.fields };
  return { kind: 'retry', message: body.message ?? `Server error ${res.status}` };
}

async function onSent(item: OutboxItem, json: { ref: string; trackingToken: string }) {
  await saveMyReport({ ref: json.ref, kind: item.kind, token: json.trackingToken, title: item.title, createdAt: item.createdAt, snapshot: json });
  await (await idb()).delete('outbox', item.id);
}

let running: Promise<number> | null = null;

/** Try to send everything pending. Returns how many were sent. Stops at the first network failure. */
export function processOutbox(): Promise<number> {
  running ??= (async () => {
    let sent = 0;
    const db = await idb();
    const items = (await db.getAll('outbox')).filter((i) => i.status === 'pending').sort((a, b) => a.createdAt - b.createdAt);
    for (const item of items) {
      try {
        const r = await send(item);
        if (r.kind === 'ok') {
          await onSent(item, r.json);
          sent++;
        } else if (r.kind === 'rejected') {
          await db.put('outbox', { ...item, status: 'failed', lastError: r.message, attempts: item.attempts + 1 });
        } else {
          await db.put('outbox', { ...item, attempts: item.attempts + 1, lastError: r.message });
        }
      } catch {
        break; // offline
      }
    }
    emit();
    return sent;
  })().finally(() => (running = null));
  return running;
}

/** Save the report on the device, then try to send it right away. */
export async function submitReport(item: Omit<OutboxItem, 'attempts' | 'status' | 'createdAt'> & { createdAt?: number }): Promise<SubmitResult> {
  const full: OutboxItem = { ...item, createdAt: item.createdAt ?? Date.now(), attempts: 0, status: 'pending' };
  const db = await idb();
  await db.put('outbox', full);
  emit();
  try {
    const r = await send(full);
    if (r.kind === 'ok') {
      await onSent(full, r.json);
      emit();
      return { status: 'sent', ref: r.json.ref, token: r.json.trackingToken, response: r.json };
    }
    if (r.kind === 'rejected') {
      await db.delete('outbox', full.id);
      emit();
      return { status: 'rejected', message: r.message, fields: r.fields };
    }
  } catch {
    /* network error: stays queued */
  }
  void requestBackgroundSync();
  return { status: 'queued' };
}

export async function pendingItems(): Promise<OutboxItem[]> {
  return (await (await idb()).getAll('outbox')).sort((a, b) => a.createdAt - b.createdAt);
}

export async function discardItem(id: string): Promise<void> {
  await (await idb()).delete('outbox', id);
  emit();
}

async function requestBackgroundSync() {
  try {
    // getRegistration() answers at once; `ready` would never resolve if the service worker hasn't
    // finished installing (first visit, or the signal dropped mid-install).
    const reg = (await navigator.serviceWorker?.getRegistration()) as (ServiceWorkerRegistration & { sync?: { register(tag: string): Promise<void> } }) | undefined;
    if (reg?.active) await reg.sync?.register('ili-outbox');
  } catch {
    /* not supported (iOS/Firefox): the page retries instead */
  }
}

/** Page-side retry loop. Call once at start-up. */
export function startOutboxSync(): void {
  const kick = () => {
    if (navigator.onLine) processOutbox().catch(() => {});
  };
  window.addEventListener('online', kick);
  document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && kick());
  setInterval(kick, 30_000);
  kick();
}
